import React from 'react';
import clsx from 'clsx';
import Chip from '@material-ui/core/Chip';
import { debounce } from '@utils';

import PlusIcon from '@assets/icons/plus-grey-dark.svg';
import useStyles from './style';

const SnippetField = ({
  classes: passedClasses = {},
  label = 'Type or select the pre-built snippets to the right.',
  onChange = () => {},
  options = [],
  value = '',
}) => {
  const wrapperRef = React.useRef(null);
  const classes = useStyles();
  const [isFocused, setIsFocused] = React.useState(false);

  const getSnippetHTML = option => {
    return `<span class="${clsx(
      classes.snippet,
      passedClasses.snippet
    )}" contenteditable="false">${option.label}</span>&nbsp;`;
  };

  const addSnippet = option => {
    if (!isFocused) {
      return;
    }

    insertSnippet(getSnippetHTML(option));
    onChange(getValue());
  };

  const insertSnippet = html => {
    wrapperRef.current && wrapperRef.current.focus();
    let sel, range;
    sel = window.getSelection();
    if (sel.getRangeAt && sel.rangeCount) {
      range = sel.getRangeAt(0);
      range.deleteContents();
      var el = document.createElement('div');
      el.innerHTML = html;
      var frag = document.createDocumentFragment(),
        node;
      while ((node = el.firstChild)) {
        frag.appendChild(node);
      }
      var firstNode = frag.firstChild;
      range.insertNode(frag);
      range.setStartAfter(firstNode);
      range.setEndAfter(firstNode);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const getValue = () => {
    if (!wrapperRef.current) {
      return '';
    }

    const innerHTML = wrapperRef.current.innerHTML;
    let value = innerHTML.replace(/<div>/g, '\n');
    value = value.replace(/<br[ ]?\/?>/g, '');
    value = value.replace(/<\/div>/g, '');
    options.forEach(option => {
      const optionRegExp = new RegExp(getSnippetHTML(option), 'g');
      value = value.replace(optionRegExp, option.value);
      const fallbackRegExp = new RegExp(
        (getSnippetHTML(option) || '').replace(/&nbsp;/g, ''),
        'g'
      );
      value = value.replace(fallbackRegExp, option.value);
    });

    value = value.replace(/&nbsp;/g, ' ');
    return value.trim();
  };

  const updateValueHTML = React.useCallback(() => {
    if (wrapperRef.current) {
      let innerHTML = value;
      options.forEach(option => {
        const optionRegExp = new RegExp(option.value, 'g');
        innerHTML = innerHTML.replace(optionRegExp, getSnippetHTML(option));
      });

      innerHTML = innerHTML.split('\n');
      let finalHTML = '';
      innerHTML.length > 0 &&
        value !== '' &&
        innerHTML.forEach(line => {
          finalHTML += `<div>${line}</div>`;
        });

      wrapperRef.current.innerHTML = finalHTML;
    }
    // eslint-disable-next-line
  }, [wrapperRef]);

  const debouncedOnchange = React.useCallback(
    debounce(() => {
      onChange(getValue());
    }, 100),
    [onChange]
  );

  React.useEffect(() => {
    updateValueHTML();
    // eslint-disable-next-line
  }, []);

  return (
    <div
      className={clsx(
        'd-flex',
        classes.wrapper,
        {
          'border-primary': isFocused,
        },
        passedClasses.wrapper
      )}
      onBlur={() => {
        setIsFocused(false);
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
    >
      <span
        className={clsx(classes.label, {
          'color-primary': isFocused,
        })}
      >
        {label}
      </span>
      <div
        className={clsx('no-border', classes.input, passedClasses.input)}
        contentEditable
        onKeyUp={() => {
          debouncedOnchange();
        }}
        ref={wrapperRef}
      ></div>
      <div
        className={clsx(
          'd-flex flex-column f-align-end',
          classes.snippetsWrapper,
          passedClasses.snippetsWrapper
        )}
      >
        {options.map((option, optionIndex) => {
          return (
            <Chip
              className='mb-1'
              classes={{
                ...passedClasses.option,
                root: clsx(
                  classes.chip,
                  passedClasses.option && passedClasses.option.root
                ),
              }}
              deleteIcon={
                <span className={classes.chipIconWrapper}>
                  <img alt='' className={classes.chipIcon} src={PlusIcon} />
                </span>
              }
              key={optionIndex}
              label={option.label}
              size='small'
              onClick={() => addSnippet(option)}
              onDelete={() => addSnippet(option)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default SnippetField;
